"use client";

import { useEffect } from "react";
import { trackEvent } from "@/lib/analytics";

// Fires GA4 `sign_up` exactly once for newly-created users.
// Fetches the user's id + createdAt from /api/user/signup-info on mount
// (no-op for unauthenticated visitors — most page loads), then checks an
// age gate and a localStorage dedupe marker before calling gtag.
// Returns null — pure side effect.
//
// Why a client fetch instead of the root layout passing server props:
// an `await auth()` in the root layout forces the entire app into dynamic
// SSR, killing CDN caching for public marketing/blog pages. Fetching here
// keeps that dynamic read scoped to this one API route.
//
// Why a render-null client component instead of firing from auth.ts:
// gtag.js lives in the browser, so the event has to come from the client.
// We can't rely on a redirect-with-flag because OAuth callbacks vary.
// Age gate keeps it from firing for returning users; dedupe marker keeps it
// from firing twice for the same signup.

// 5-minute window — generous enough to survive OAuth redirects and a slow
// post-signup page load, narrow enough that returning users never trip it.
const SIGNUP_AGE_WINDOW_MS = 5 * 60 * 1000;

export function GA4SignupTracker() {
  useEffect(() => {
    if (typeof window === "undefined") return;

    let cancelled = false;

    fetch("/api/user/signup-info")
      .then((res) => (res.ok ? res.json() : null))
      .then((data: { userId?: string; createdAtMs?: number } | null) => {
        if (cancelled || !data?.userId || !data.createdAtMs) return;

        const { userId, createdAtMs } = data;
        const age = Date.now() - createdAtMs;
        if (age < 0 || age > SIGNUP_AGE_WINDOW_MS) return;

        const marker = `ga4_sign_up_fired_${userId}`;
        if (window.localStorage.getItem(marker)) return;

        trackEvent("sign_up", { method: "google", plan: "youguard" });
        window.localStorage.setItem(marker, String(Date.now()));
      })
      .catch(() => {
        // Best-effort analytics — swallow fetch failures.
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return null;
}
